import { getUrls } from '@shared/utils/config/environment';
import { ChainId, WRAPPED_SOL } from '@shared/utils/config/networks';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from 'usehooks-ts';
export async function userBalanceERC20({
  address,
  solanaAddress,
  // Using solana chainId from Covalent here not defined
  chains = [1, 137, 10, 56, 43114, 42161, 250, 1399811149],
  useGeniusAPI
}) {
  var _a, _b, _c;
  if (useGeniusAPI === true) {
    const evmAssetsPromise = fetch(`${getUrls().INDEXER_URL}/portfolio/assets?address=${address}`);
    const svmAssetsPromise = fetch(`${getUrls().INDEXER_URL}/portfolio/assets?address=${solanaAddress}&chains=${1399811149}`);
    const [evmAssets, svmAssets] = await Promise.all([evmAssetsPromise, svmAssetsPromise]);
    const payload = await evmAssets.json();
    if (payload.status == 'error') {
      throw new Error('Failed to fetch user balance');
    }
    const svmPayload = await svmAssets.json();
    if (svmPayload.status == 'error') {
      throw new Error('Failed to fetch user balance');
    }
    const svmIndex = payload === null || payload === void 0 ? void 0 : payload.data.findIndex(x => x.chainId == 1399811149);
    payload.data[svmIndex] = svmPayload === null || svmPayload === void 0 ? void 0 : svmPayload.data[0];
    payload['solanaAddress'] = solanaAddress;
    payload.totalUsdValue += svmPayload === null || svmPayload === void 0 ? void 0 : svmPayload.data[0].totalUsdValue;
    payload.totalUsdValue24h += svmPayload === null || svmPayload === void 0 ? void 0 : svmPayload.data[0].totalUsdValue24h;
    payload.data.forEach(chainData => {
      var _a;
      chainData['balances'] = (_a = chainData === null || chainData === void 0 ? void 0 : chainData.balances) === null || _a === void 0 ? void 0 : _a.map(balance => {
        if (balance.contract_address == '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && (balance.network == ChainId.SOLANA || balance.network == ChainId.SOLANA_DLN)) {
          balance.contract_address = WRAPPED_SOL;
        }
        return Object.assign({}, balance);
      });
    });
    const flattenedBalances = (_a = payload.data.map(chainBalances => {
      return chainBalances.balances;
    }).flat()) === null || _a === void 0 ? void 0 : _a.sort((a, b) => {
      if (!a.usdValue && !b.usdValue) {
        return 0;
      }
      if (!a.usdValue) {
        return 1;
      }
      if (!b.usdValue) {
        return -1;
      }
      return b.usdValue - a.usdValue;
    });
    return {
      balancesByChain: payload.data,
      balances: flattenedBalances,
      totalUsdValue: payload === null || payload === void 0 ? void 0 : payload.totalUsdValue,
      totalUsdValue24h: payload === null || payload === void 0 ? void 0 : payload.totalUsdValue24h
    };
  } else {
    const chainIds = chains.join(',');
    const url = `/api/user/erc20?chains=${chainIds}&address=${address}&solana_address=${solanaAddress}`;
    const response = await fetch(url);
    const payload = await response.json();
    if (!payload || !payload.data || payload.error) {
      throw new Error('Failed to fetch user balance');
    }
    const responseBalances = payload === null || payload === void 0 ? void 0 : payload.data;
    const tokenBalances = (_b = responseBalances === null || responseBalances === void 0 ? void 0 : responseBalances.map(chainData => {
      var _a;
      const totalUsdValue = (_a = chainData === null || chainData === void 0 ? void 0 : chainData.balances) === null || _a === void 0 ? void 0 : _a.reduce((acc, balance) => {
        var _a;
        return acc + ((_a = balance.usd_value) !== null && _a !== void 0 ? _a : 0);
      }, 0);
      return Object.assign(Object.assign({}, chainData), {
        totalUsdValue,
        totalUsdRate: 0
      });
    })) === null || _b === void 0 ? void 0 : _b.sort((a, b) => {
      if (!(a === null || a === void 0 ? void 0 : a.totalUsdValue) && !(b === null || b === void 0 ? void 0 : b.totalUsdValue)) {
        return 0;
      }
      if (!(a === null || a === void 0 ? void 0 : a.totalUsdValue)) {
        return 1;
      }
      if (!(b === null || b === void 0 ? void 0 : b.totalUsdValue)) {
        return -1;
      }
      return (b === null || b === void 0 ? void 0 : b.totalUsdValue) - (a === null || a === void 0 ? void 0 : a.totalUsdValue);
    });
    const flattenedBalances = (_c = tokenBalances.map(chainBalances => {
      return chainBalances.balances;
    }).flat()) === null || _c === void 0 ? void 0 : _c.sort((a, b) => {
      if (!a.usdValue && !b.usdValue) {
        return 0;
      }
      if (!a.usdValue) {
        return 1;
      }
      if (!b.usdValue) {
        return -1;
      }
      return b.usdValue - a.usdValue;
    });
    const {
      totalUsdValue,
      totalUsdValue24h
    } = flattenedBalances.reduce(({
      totalUsdValue,
      totalUsdValue24h
    }, balance) => {
      return {
        totalUsdValue: totalUsdValue + (Number.isNaN(balance === null || balance === void 0 ? void 0 : balance.quote) ? 0 : balance === null || balance === void 0 ? void 0 : balance.quote),
        totalUsdValue24h: totalUsdValue24h + (Number.isNaN(balance === null || balance === void 0 ? void 0 : balance.quote_24h) ? 0 : balance === null || balance === void 0 ? void 0 : balance.quote_24h)
      };
    }, {
      totalUsdValue: 0,
      totalUsdValue24h: 0
    });
    tokenBalances.forEach(item => item.totalUsdRate = !totalUsdValue ? 0 : (item === null || item === void 0 ? void 0 : item.totalUsdValue) * 100 / totalUsdValue);
    return {
      balancesByChain: tokenBalances,
      balances: flattenedBalances,
      totalUsdValue,
      totalUsdValue24h
    };
  }
}
export default function useERC20Balance({
  evmAddresses,
  solanaAddresses,
  chains = [1, 137, 10, 56, 43114, 42161, 8453, 1399811149]
}) {
  const [, setLastFetchedPortfolio] = useLocalStorage(`lastFetchedPortfolio`, '');
  const [portfolioDataLC, setPortfolioDataLC] = useLocalStorage(`portfolioData`, null);
  //TODO refactor endpoint to handle multiple addresses
  const query = useQuery({
    queryKey: ['userbalance', evmAddresses, chains, solanaAddresses],
    queryFn: async () => {
      const data = await userBalanceERC20({
        address: evmAddresses === null || evmAddresses === void 0 ? void 0 : evmAddresses[0],
        chains,
        useGeniusAPI: true,
        solanaAddress: solanaAddresses === null || solanaAddresses === void 0 ? void 0 : solanaAddresses[0]
      });
      setLastFetchedPortfolio(new Date().toISOString());
      // const _transformedBalances = data.balances.map((token) => {
      //   if (token.contract_address?.toLocaleLowerCase() === NATIVE_ADDRESS?.toLocaleLowerCase()) {
      //     token.contract_address = WrappedNativeTokens.get(token.network);
      //   }
      //   return token;
      // });
      // const _transformedBalancesByChain = data.balancesByChain.map((chain) => {
      //   const _balances = chain.balances.map((token) => {
      //     if (token.contract_address?.toLocaleLowerCase() === NATIVE_ADDRESS?.toLocaleLowerCase()) {
      //       token.contract_address = WrappedNativeTokens.get(token.network);
      //     }
      //     return token;
      //   });
      //   return {
      //     ...chain,
      //     balances: _balances,
      //   };
      // });
      setPortfolioDataLC(Object.assign({}, data));
      return Object.assign({}, data);
    },
    enabled: !!(evmAddresses === null || evmAddresses === void 0 ? void 0 : evmAddresses[0]),
    refetchInterval: 5000,
    gcTime: 1000 * 60 * 60 * 24 * 7
  });
  return Object.assign(Object.assign({}, query), {
    data: portfolioDataLC || query.data,
    isLoading: !portfolioDataLC && query.isLoading
  });
}